import React, {
  useMemo,
} from 'react';
import InputText from 'components/common/InputText';
import { FieldComponentProps } from 'components/anvilFormFillable/fields/types';
import AddressSuggestion from 'components/common/AddressSuggestion';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {
  US_STATES,
  USStateType,
} from 'lib/usStateOptions';
import ZipCodeTextField from 'components/common/ZipCodeTextField';
import {
  getAddressString,
  isEmptyString,
} from 'utils/helper';
import {
  Controller,
  useFormContext,
} from 'react-hook-form';
import clsx from 'clsx';
import { AddressDTO } from 'artifacts/dtos';
import { formatPlace } from 'utils/misc';
import { getFieldStateKey } from 'utils/signaturePrep';

export default function USAddress({
  field: _field,
}: FieldComponentProps) {
  const {
    control,
    setValue,
    trigger,
    watch,
  } = useFormContext();
  const fieldId = _field?.id ?? '';
  const fieldStateKey = getFieldStateKey(_field);
  const valueBase = `${fieldStateKey}.value`;
  const street = watch(`${valueBase}.street`);
  const unit = watch(`${valueBase}.unit`);
  const city = watch(`${valueBase}.city`);
  const state = watch(`${valueBase}.state`);
  const zipCode = watch(`${valueBase}.zipCode`);

  const addressValue = useMemo<AddressDTO>(() => ({
    street: street ?? '',
    unit: unit ?? '',
    city: city ?? '',
    state: state ?? '',
    zipCode: zipCode ?? '',
  }), [city, state, street, unit, zipCode]);

  const selectedState = useMemo<USStateType | null>(() => (
    isEmptyString(state) ? null : (
      US_STATES.find(
        (s) => s.abbreviation.toUpperCase() === state.toUpperCase()
          || s.name.toLowerCase() === state.toLowerCase(),
      ) ?? null
    )), [state]);

  const suggestionValue = useMemo(
    () => (getAddressString(addressValue) === '--' ? null : formatPlace(addressValue).place),
    [addressValue],
  );

  const handleAddressSuggestion = async (address: AddressDTO) => {
    const nextAddress = {
      ...address,
      unit: unit ?? '',
      state: address?.state?.toUpperCase() ?? '',
      zipCode: address?.shortZipCode ?? address?.zipCode ?? '',
    };

    setValue(`${valueBase}.street`, nextAddress.street ?? '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    setValue(`${valueBase}.city`, nextAddress.city ?? '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    setValue(`${valueBase}.state`, nextAddress.state ?? '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    setValue(`${valueBase}.zipCode`, nextAddress.zipCode ?? '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });

    await trigger([
      `${valueBase}.street`,
      `${valueBase}.city`,
      `${valueBase}.state`,
      `${valueBase}.zipCode`,
    ]);
  };

  return (
    <div className="space-y-3 w-full">
      <div className="w-full">
        <AddressSuggestion
          autoFocus
          className="!w-full"
          value={suggestionValue}
          onChange={handleAddressSuggestion}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-10 lg:grid-cols-16">
        <Controller
          name={`${valueBase}.street`}
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState: { invalid } }) => (
            <div className={clsx('sm:col-span-4 lg:col-span-5', { 'has-error': invalid })}>
              <InputText
                {...field}
                id={`${_field?.id ?? ''}-street`}
                placeholder="Street"
                maxLength={64}
                className="w-full"
              />
            </div>
          )}
        />
        <Controller
          name={`${valueBase}.unit`}
          control={control}
          render={({ field }) => (
            <div className="sm:col-span-2 lg:col-span-2">
              <InputText
                {...field}
                id={`${_field?.id ?? ''}-unit`}
                placeholder="Unit, Apt, Suite"
                maxLength={32}
                onBlur={field.onBlur}
                onChange={(e) => field.onChange(e.target.value)}
                className="w-full"
              />
            </div>
          )}
        />
        <Controller
          name={`${valueBase}.city`}
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState: { invalid } }) => (
            <div className={clsx('sm:col-span-2 lg:col-span-3', { 'has-error': invalid })}>
              <InputText
                {...field}
                id={`${_field?.id ?? ''}-city`}
                placeholder="City"
                maxLength={64}
                className="w-full"
              />
            </div>
          )}
        />
        <Controller
          name={`${valueBase}.state`}
          control={control}
          rules={{ required: true, validate: (v) => /^[A-Za-z]{2}$/.test((v ?? '').toString()) }}
          render={({ field, fieldState: { invalid } }) => (
            <div className={clsx('sm:col-span-2 lg:col-span-4', { 'has-error': invalid })}>
              <Autocomplete<USStateType>
                options={US_STATES}
                value={selectedState}
                onChange={(_, option) => {
                  field.onChange(option?.abbreviation ?? '');
                }}
                getOptionLabel={(option) => `${option?.name} - ${option?.abbreviation}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id={`${_field?.id ?? ''}-state`}
                    inputProps={{
                      ...params.inputProps,
                      placeholder: 'State',
                    }}
                  />
                )}
                className="w-full"
              />
            </div>
          )}
        />
        <Controller
          name={`${valueBase}.zipCode`}
          control={control}
          rules={{
            required: true,
            validate: (v) => /^\d{5}$/.test((v ?? '').toString()),
          }}
          render={({ field, fieldState: { invalid } }) => (
            <div className={clsx('sm:col-span-2 lg:col-span-2', { 'has-error': invalid })}>
              <ZipCodeTextField
                {...field}
                id={`${_field?.id ?? ''}-zipCode`}
                className="w-full"
                inputProps={{
                  placeholder: 'ZIP Code',
                }}
              />
            </div>
          )}
        />
      </div>
    </div>
  );
}
